<template>
  <teleport
    v-if="isDataLoaded && volumesData.length != 0"
    to="#headerPageContent"
  >
    <HeaderBarItem>
      <AnalyticsFilterDropdown
        v-model="model.filter"
        :schema="schema.filter"
        :schema-options="schemaOptions"
        :reset-all="resetAll"
        :apply-filters="applyFilters"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <AnalyticsPenetrationRateDropdown
        v-model="penetrationRateModel.filter"
        :schema="penetrationRateSchema.filter"
        :schema-options="penetrationRateSchemaOptions"
        :reset-all="resetAllPenetrationRate"
        :compute-penetration-rate="computePenetrationRate"
        :is-disabled="disablePenetrationRate"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <AnalyticsSplitDropdown
        v-model="model.split"
        :schema="schema.split"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <AnalyticsDetailsDropdown
        v-model="model.details"
        :schema="schema.details"
        :is-disabled="disableDetails"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <AnalyticsExportDropdown
        @exportExcel="exportExcel"
      />
    </HeaderBarItem>
  </teleport>
  <div :class="$style.page">
    <BaseContainer
      :is-paddingless="expandedChartIndex == null && model?.split != null"
    >
      <div :class="$style.filtersContainer">
        <span
          v-if="isDataLoaded && selectedFilters.length === 0 && volumesData.length != 0"
          :class="$style.noFilterLabel"
        >
          No filter applied
        </span>
        <div
          v-for="selectedFilter of selectedFilters"
          :key="selectedFilter.label"
          :class="$style.filterContainer"
        >
          <div
            v-if="selectedFilter.label=='Production Dates'"
            :class="$style.filtersContainer"
          >
            <span>{{ selectedFilter.label }} </span>
            <BaseLabel
              v-for="value of selectedFilter.values"
              :key="value"
              color="aqua"
              :class="$style.filterLabel"
            >
              {{ value.start ? format(new Date(value.start), 'MMMM y') : '' }} -
              {{ value.end ? format(new Date(value.end), 'MMMM y') : '' }}
            </BaseLabel>
          </div>
          <div
            v-else
            :class="$style.filtersContainer"
          >
            <span>{{ selectedFilter.label }} </span>
            <BaseLabel
              v-for="value of selectedFilter.values"
              :key="value"
              color="aqua"
              :class="$style.filterLabel"
            >
              {{ value }}
            </BaseLabel>
          </div>
        </div>
      </div>

      <template v-if="!isDataLoaded">
        <BaseLoader
          is-block
          is-active
        />
      </template>
      <template
        v-else-if="volumesData && volumesData.length != 0"
      >
        <template v-if="model.split == null">
          <VolumesChart
            :labels="charts[0].labels"
            :datasets="charts[0].datasets"
            :custom-options="charts[0].options"
            :class="$style.singleChart"
            :type="chartType"
            has-cagr
            has-big-heading
          >
            <template #heading>
              {{ charts[0].label }}
            </template>
          </VolumesChart>
        </template>
        <template v-else>
          <div
            v-show="expandedChartIndex == null"
            :class="[
              $style.chartsGrid,
              $style[`last${charts.length % 3}`]
            ]"
          >
            <div
              v-for="(chart, index) in charts"
              :key="chart.label"
              :class="$style.chartBlock"
            >
              <button
                :class="$style.expand"
                type="button"
                @click="expandChart(index)"
              >
                <ExpandSvg />
              </button>
              <VolumesChart
                :labels="chart.labels"
                :datasets="chart.datasets"
                :aspect-ratio="1"
                :suggested-y-max="maxChartValue"
                :custom-options="chart.options"
                :type="chartType"
                has-cagr
              >
                <template #heading>
                  {{ chart.label }}
                </template>
              </VolumesChart>
            </div>
          </div>
          <VolumesChart
            v-if="expandedChartIndex != null"
            :labels="charts[expandedChartIndex].labels"
            :datasets="charts[expandedChartIndex].datasets"
            :class="$style.singleChart"
            :type="chartType"
            has-big-heading
            has-close-button
            has-cagr
            @close="expandChart(null)"
          >
            <template #heading>
              {{ charts[expandedChartIndex].label }}
            </template>
          </VolumesChart>
        </template>
      </template>
      <template
        v-else
      >
        <FormBox
          heading="No data available"
        />
      </template>
    </BaseContainer>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { cloneDeep, difference } from 'lodash';
import { mapState } from 'vuex';
import excelJS from 'exceljs';
import BaseContainer from '@/components/base/Container';
import BaseLoader from '@/components/base/Loader';
import FormBox from '@/components/FormBox';
import VolumesChart from '@/components/VolumesChart';
import HeaderBarItem from '@/components/HeaderBarItem';
import AnalyticsSplitDropdown from '@/components/AnalyticsSplitDropdown';
import AnalyticsDetailsDropdown from '@/components/AnalyticsDetailsDropdown';
import AnalyticsFilterDropdown from '@/components/AnalyticsFilterDropdown';
import AnalyticsExportDropdown from '@/components/AnalyticsExportDropdown';
import AnalyticsPenetrationRateDropdown from '@/components/AnalyticsPenetrationRateDropdown';
import ExpandSvg from '@/assets/images/icons/expand.svg?inline';
import * as chartTools from '@/charts';
import BaseLabel from '@/components/base/Label';

export default {
  components: {
    BaseContainer,
    BaseLoader,
    FormBox,
    VolumesChart,
    AnalyticsFilterDropdown,
    AnalyticsSplitDropdown,
    AnalyticsDetailsDropdown,
    AnalyticsExportDropdown,
    AnalyticsPenetrationRateDropdown,
    HeaderBarItem,
    ExpandSvg,
    BaseLabel,
  },
  props: {
    volumesType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isDataLoaded: false,
      expandedChartIndex: null,
      volumesData: null,
      model: null,
      schema: null,
      schemaOptions: null,
      resetAllFilters: false,
      filters: null,
      selectedFilters: [],
      penetrationRateSchema: null,
      penetrationRateSchemaOptions: null,
      penetrationRateModel: null,
      resetAllPenetrationRateStatus: false,
      penetrationRateField: null,
      penetrationRateItems: null,
      chartType: 'line',
      disableDetails: false,
      disablePenetrationRate: false,
    };
  },
  computed: {
    sourceTools() {
      switch (this.volumesType) {
        case 'Cars':
          return chartTools.sourcesMap.carVolumes;
        case 'LightingDevices':
          return chartTools.sourcesMap.lightingDeviceVolumes;
        default:
          return null;
      }
    },
    charts() {
      if (!this.isDataLoaded) return [];
      if (this.volumesData && this.volumesData.length !== 0) {
        let filteredData = null;
        if (this.filters) {
          filteredData = this.sourceTools.applyFilter(this.volumesData, this.filters);
        } else {
          filteredData = this.volumesData;
        }
        return this.sourceTools.getCharts(
          filteredData,
          this.model.details,
          this.model.split,
          false,
          this.penetrationRateItems,
        );
      }
      return [];
    },
    maxChartValue() {
      let max = -Infinity;

      this.charts.forEach((chart) => {
        chart.datasets.forEach((dataset) => {
          dataset.data.forEach((data) => {
            if (data.y.value > max) max = data.y.value;
          });
        });
      });

      return max;
    },
    ...mapState('user', ['user']),
    ...mapState('app', ['updateReferenceId']),
  },
  watch: {
    'model.filter': {
      deep: true,
      handler: async function modelFilterChanged(prevValue, newValue) {
        const changedField = { field: null, direction: null };
        if (newValue) {
          const deepPrevValue = cloneDeep(prevValue);
          const deepNewValue = cloneDeep(newValue);
          Object.keys(deepNewValue).filter((item) => {
            if (difference(deepPrevValue[item], deepNewValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'add';
            }

            if (difference(deepNewValue[item], deepPrevValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'remove';
            }

            return true;
          });
        }
        if (changedField.field) {
          this.resetAllFilters = false;
          this.loadDynamicSchemaOptions(changedField);
        }
      },
    },
    'model.details': {
      deep: true,
      handler: async function modelDetailsChanged() {
        if (this.model.details.length === 0) {
          this.disablePenetrationRate = false;
        }

        if (this.model.details.length === 1) {
          this.disablePenetrationRate = true;
        }
      },
    },
    'penetrationRateModel.filter': {
      deep: true,
      handler: async function modelPenetrationRateChanged(prevValue, newValue) {
        const changedField = { field: null, direction: null };
        if (newValue) {
          const deepPrevValue = cloneDeep(prevValue);
          const deepNewValue = cloneDeep(newValue);
          Object.keys(deepNewValue).filter((item) => {
            if (difference(deepPrevValue[item], deepNewValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'add';
            }

            if (difference(deepNewValue[item], deepPrevValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'remove';
            }

            return true;
          });
        }
        if (changedField.field) {
          if (this.resetAllPenetrationRateStatus) {
            this.resetAllPenetrationRateStatus = false;
          } else {
            this.penetrationRateField = changedField.field;
            this.penetrationRateSchema.filter = this.penetrationRateSchema.filter.map((item) => {
              let modifiedItem = item;
              if (item.name !== changedField.field) {
                modifiedItem = {
                  ...item,
                  disable: true,
                };
              }
              return modifiedItem;
            });
          }
        }
      },
    },
  },
  async created() {
    this.schema = await this.sourceTools.getSchema();
    this.penetrationRateSchema = await this.sourceTools.getSchema();
    this.model = await this.sourceTools.getModel();
    this.penetrationRateModel = await this.sourceTools.getModel();
    this.volumesData = await this.sourceTools.getData(this.user.company.uuid, this.updateReferenceId);
    this.schemaOptions = await this.sourceTools.getOptions();
    this.penetrationRateSchemaOptions = await this.sourceTools.getOptions();
    this.isDataLoaded = true;
  },
  methods: {
    format,
    async loadDynamicSchemaOptions(changedField) {
      if (this.resetAllFilters) return false;
      if (this.sourceTools.getDynamicOptions && this.volumesData) {
        if (!this.isDataLoaded) {
          this.schemaOptions = {
            ...this.schemaOptions,
          };
        } else {
          this.schemaOptions = {
            ...this.schemaOptions,
            ...(await this.sourceTools.getDynamicOptions(this.model.filter, this.volumesData, changedField)),
          };
        }
      }
      return true;
    },
    setSelectedFilter() {
      this.selectedFilters = this.schema.filter.reduce((activeFilters, item) => {
        if (item.type === 'dateRange') {
          const value = this.model.filter[item.name];
          if (value.start != null || value.end != null) {
            activeFilters.push({
              label: item.label,
              values: [value],
            });
          }
        } else if (item.type === 'select') {
          if (this.model.filter[item.name].length > 0) {
            activeFilters.push({
              label: item.label,
              values: this.model.filter[item.name],
            });
          }
        }

        return activeFilters;
      }, []);
    },
    expandChart(index) {
      this.expandedChartIndex = index;
    },
    generateExcel() {
      const workbook = new excelJS.Workbook();
      workbook.creator = 'https://lightingmi.com/';
      workbook.lastModifiedBy = 'LMI';
      workbook.created = new Date();
      workbook.modified = new Date();

      this.charts.forEach((chart) => {
        const sheet = workbook.addWorksheet(chart.label);
        const firstRow = sheet.getRow(1);
        const firstYear = chart.labels[0];
        chart.labels.forEach((label, index) => {
          firstRow.getCell(2 + index).value = label;
        });

        chart.datasets.forEach((dataset, index) => {
          const row = sheet.getRow(2 + index);
          row.getCell(1).value = dataset.label;

          dataset.data.forEach((datum) => {
            row.getCell(2 + datum.x - firstYear).value = datum.y.value;
          });
        });
      });

      return workbook;
    },
    downloadFileFromBuffer(buffer, filename) {
      const blob = new Blob([buffer]);
      const link = document.createElement('a');
      link.setAttribute('download', filename);
      link.href = URL.createObjectURL(blob);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    exportExcel() {
      const wb = this.generateExcel();

      wb.xlsx
        .writeBuffer({
          base64: true,
        })
        .then((buffer) => {
          this.downloadFileFromBuffer(buffer, `LMI export ${new Date()}.xlsx`);
        });
    },
    async resetAll() {
      this.resetAllFilters = true;
      this.schemaOptions = await this.sourceTools.getOptions();
      this.model.filter = await this.sourceTools.getModel().filter;
      this.filters = this.model.filter;
      this.setSelectedFilter();
    },
    applyFilters() {
      this.filters = this.model.filter;
      this.setSelectedFilter();
    },
    async resetAllPenetrationRate() {
      this.resetAllPenetrationRateStatus = true;
      this.penetrationRateModel.filter = await this.sourceTools.getModel().filter;
      this.penetrationRateSchema.filter = this.penetrationRateSchema.filter.map((item) => {
        const result = {
          ...item,
          disable: false,
        };
        return result;
      });
      this.chartType = 'line';
      this.penetrationRateField = null;
      this.penetrationRateItems = null;
      this.disableDetails = false;
    },
    computePenetrationRate() {
      this.penetrationRateItems = {
        field: this.penetrationRateField,
        items: this.penetrationRateModel.filter[this.penetrationRateField],
      };
      this.chartType = 'bar';
      this.disableDetails = true;
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-bottom: 20px;
}

.chartsGrid {
  display: grid;
  grid-template-columns: repeat(3, calc(100% / 3));
  padding: 0 20px;

  &.last0,
  &.last1,
  &.last2 {

    .chartBlock {

      &:nth-last-child(1) {
        border-bottom: 0;
      }
    }
  }

  &.last0,
  &.last2 {

    .chartBlock {

      &:nth-last-child(2) {
        border-bottom: 0;
      }
    }
  }

  &.last0 {

    .chartBlock {

      &:nth-last-child(3) {
        border-bottom: 0;
      }
    }
  }
}

.singleChart {
  margin-bottom: 15px;
}

.chartBlock {
  position: relative;
  padding: 15px 20px;
  border-right: 1px solid rgba($disabled, 0.4);
  border-bottom: 1px solid rgba($disabled, 0.4);

  &:nth-child(3n) {
    border-right: 0;
  }
}

.expand {
  @include reset;
  position: absolute;
  top: 5px;
  right: 5px;
  display: inline-flex;
  padding: 5px;
  color: $disabled;
  cursor: pointer;

  svg {
    width: 10px;
    height: auto;
  }
}

.box {
  width: 100%;
}

.filtersContainer {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  min-height: 40px;
  margin-bottom: 10px;
}

.noFilterLabel {
  font-weight: bold;
}

.filterContainer {
  margin-left: 10px;
  font-weight: bold;
}

.filterLabel {
  margin-top: 5px;
  margin-left: 3px;
}

.hide {
  display: none;
}
</style>
